import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  ArrowUpRight,
  AudioLines,
  Blocks,
  BookOpen,
  Bot,
  Briefcase,
  ChevronDown,
  ChevronsLeft,
  Clock,
  Database,
  FileText,
  FlaskConical,
  GitBranch,
  GitFork,
  GitMerge,
  Globe,
  History,
  Home,
  LayoutGrid,
  Loader2,
  type LucideIcon,
  MessageSquare,
  Palette,
  Rocket,
  Settings,
  Shield,
  Sparkles,
  TerminalSquare,
  Type,
  User,
  Users,
  Video,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { caseKeys, useCaseSettings } from '@/hooks/use-case-queries'
import { useEnabledExtensionIds } from '@/hooks/use-extensions'
import { api } from '@/services/api'
import { cn } from '@/utils/cn'
import { HOME_ROUTE, type Section } from './sections'
import {
  hiddenSubViewPaths,
  resolveSubViewUrl,
  type SubView,
  visibleWorkspaces,
  type Workspace,
} from './workspaces'

interface CaseSummary {
  id: string
  name: string
  status?: string
  document_count?: number
}

function SectionHeader({
  title,
  showCollapse,
  onCollapse,
}: {
  title: string
  showCollapse: boolean
  onCollapse?: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-shell-header shrink-0 items-center gap-2 border-b border-border px-4">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      <div className="flex-1" />
      {showCollapse && (
        <button
          type="button"
          onClick={onCollapse}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={t('nav.collapseSidebar')}
        >
          <ChevronsLeft className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  )
}

function NavRow({
  icon: Icon,
  label,
  active,
  onClick,
  indent = 0,
  accent,
}: {
  icon?: LucideIcon
  label: string
  active?: boolean
  onClick?: () => void
  indent?: number
  accent?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ paddingLeft: 12 + indent * 12 }}
      className={cn(
        'flex w-full items-center gap-2 rounded py-1.5 pr-3 text-left text-sm transition-colors',
        active
          ? 'bg-primary/10 font-medium text-foreground'
          : accent
            ? 'font-medium text-sky-600 hover:bg-sky-500/10 dark:text-sky-400'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
    >
      {Icon && (
        <Icon
          className={cn(
            'h-4 w-4 shrink-0',
            active && 'text-primary',
            !active && accent && 'text-sky-600 dark:text-sky-400'
          )}
        />
      )}
      <span className="flex-1 truncate">{label}</span>
    </button>
  )
}

const SUBVIEW_KEY_MAP: Record<string, string> = {
  overview: 'overview',
  agent: 'agent',
  documents: 'documents',
  'extraction-runs': 'extractionRuns',
  schema: 'caseSchema',
  insights: 'insights',
  identity: 'identity',
  'entity-resolution': 'entityResolution',
  data: 'data',
  'knowledge-graph': 'knowledgeGraph',
  'explore-graph': 'exploreGraph',
  graph: 'graph',
  geolocation: 'geolocation',
  timeline: 'timeline',
  'time-axis': 'timeAxis',
  'case-summary': 'caseSummary',
  evidence: 'evidencePackage',
  audit: 'auditTrail',
}

/* ───────────────────────── Cases sidebar ───────────────────────── */

function CasePicker({
  caseId,
  onOpenChange,
}: {
  caseId: string | undefined
  onOpenChange?: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const casesQuery = useQuery<CaseSummary[]>({
    queryKey: caseKeys.list,
    queryFn: () => api.cases.list() as Promise<CaseSummary[]>,
  })
  const activeCase = caseId ? casesQuery.data?.find(c => c.id === caseId) : undefined
  const others = (casesQuery.data ?? []).filter(c => c.id !== caseId).slice(0, 20)

  return (
    <div className="px-2 pb-1 pt-1">
      <DropdownMenu onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm transition-colors hover:border-primary/40"
          >
            <div
              className={cn(
                'flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold',
                activeCase ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
              )}
            >
              {activeCase ? activeCase.name.slice(0, 1).toUpperCase() : '?'}
            </div>
            <span
              className={cn(
                'flex-1 truncate text-left',
                activeCase ? 'font-medium text-foreground' : 'text-muted-foreground'
              )}
            >
              {activeCase ? activeCase.name : t('nav.cases.selectCase')}
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={4} className="w-56">
          {casesQuery.isLoading && (
            <div className="flex items-center justify-center px-3 py-3 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
            </div>
          )}
          {!casesQuery.isLoading && others.length === 0 && !activeCase && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {t('nav.cases.noCasesYet')}
            </div>
          )}
          {others.map(c => (
            <DropdownMenuItem
              key={c.id}
              onClick={() => {
                const prefix = caseId ? `/cases/${caseId}/` : null
                const subPath =
                  prefix && pathname.startsWith(prefix) ? pathname.slice(prefix.length) : ''
                navigate(`/cases/${c.id}/${subPath}`)
              }}
              className="text-xs"
            >
              <span className="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-sm bg-muted text-[10px] font-medium uppercase text-muted-foreground">
                {c.name.slice(0, 1)}
              </span>
              {c.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function CasesSidebarBody({
  caseId,
  activeWorkspace,
  activeSubView,
  pathname,
  onPickerOpenChange,
}: {
  caseId: string | undefined
  activeWorkspace: Workspace | undefined
  activeSubView: SubView | undefined
  pathname: string
  onPickerOpenChange?: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: caseSettings } = useCaseSettings(caseId ?? '')
  const workspaces = visibleWorkspaces(useEnabledExtensionIds(), hiddenSubViewPaths(caseSettings))

  return (
    <>
      <div className="flex flex-col gap-0.5 px-2 pb-1 pt-2">
        <NavRow
          icon={Home}
          label={t('nav.cases.home')}
          active={pathname === HOME_ROUTE}
          onClick={() => {
            if (pathname !== HOME_ROUTE) navigate(HOME_ROUTE)
          }}
        />
      </div>

      <div className="mt-2 border-t border-border pt-2">
        <div className="px-3 pb-1 text-[11px] text-muted-foreground">
          {t('nav.cases.currentCase')}
        </div>
        <CasePicker caseId={caseId} onOpenChange={onPickerOpenChange} />
      </div>

      {caseId && (
        <div className="flex flex-col pt-1">
          {workspaces.map(ws => {
            const isActive = ws.key === activeWorkspace?.key
            // Single-view workspaces render flat without a group header (except 'report' which always shows as a group).
            if (ws.subViews.length === 1 && ws.key !== 'report') {
              const sv = ws.subViews[0]
              const url = resolveSubViewUrl(caseId, sv)
              return (
                <div key={ws.key} className="px-2">
                  <NavRow
                    icon={ws.key === 'agent' ? Bot : ws.icon}
                    label={t(`nav.workspaces.${ws.key}`)}
                    active={isActive}
                    accent={ws.key === 'agent'}
                    onClick={() => {
                      if (pathname !== url) navigate(url)
                    }}
                  />
                </div>
              )
            }

            // Multi-view workspaces render as a non-clickable group header + flat items.
            return (
              <div key={ws.key} className="mt-1">
                <GroupLabel>{t(`nav.workspaces.${ws.key}`)}</GroupLabel>
                <div className="flex flex-col gap-0.5 px-2">
                  {ws.subViews.map(sv => {
                    const subActive = isActive && activeSubView?.path === sv.path
                    const url = resolveSubViewUrl(caseId, sv)
                    const svKey = SUBVIEW_KEY_MAP[sv.path] ?? sv.path
                    return (
                      <NavRow
                        key={sv.path}
                        icon={sv.icon}
                        label={t(`nav.subviews.${svKey}`)}
                        active={subActive}
                        onClick={() => {
                          if (pathname !== url) navigate(url)
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

/* ───────────────────────── Data / Admin / Settings sidebars ───────────────────────── */

interface SimpleItem {
  path: string
  label: string
  icon: LucideIcon
}

// `/docs/extraction-demo` is intentionally NOT here — it's an interactive
// mini-app, surfaced via the featured launcher at the top of the docs sidebar.
const DOCS_TOP_ITEMS: SimpleItem[] = [
  { path: '/docs', label: 'Overview', icon: BookOpen },
  { path: '/docs/getting-started', label: 'Getting Started', icon: Rocket },
]

const DOCS_GUIDE_ITEMS: SimpleItem[] = [
  { path: '/docs/case-management', label: 'Case Management', icon: LayoutGrid },
  {
    path: '/docs/document-processing',
    label: 'Document Processing',
    icon: FileText,
  },
  { path: '/docs/graph-explorer', label: 'Graph Explorer', icon: GitFork },
  {
    path: '/docs/natural-language',
    label: 'Natural Language',
    icon: MessageSquare,
  },
  {
    path: '/docs/entity-resolution',
    label: 'Entity Resolution',
    icon: GitMerge,
  },
  {
    path: '/docs/investigation-analysis',
    label: 'Investigation Analysis',
    icon: Sparkles,
  },
  { path: '/docs/timeline-view', label: 'Timeline View', icon: Clock },
  { path: '/docs/long-horizon-file', label: 'Long-horizon File', icon: History },
]

const DOCS_PIPELINE_ITEMS: SimpleItem[] = [
  { path: '/docs/pipelines', label: 'Overview', icon: GitBranch },
  { path: '/docs/pipelines/text', label: 'Text', icon: Type },
  { path: '/docs/pipelines/tabular', label: 'Tabular', icon: Database },
  { path: '/docs/pipelines/geospatial', label: 'Geospatial', icon: Globe },
]

const DOCS_SCHEMA_ITEMS: SimpleItem[] = [
  {
    path: '/docs/schema-conventions',
    label: 'Schema Conventions',
    icon: BookOpen,
  },
  { path: '/docs/audit-schema', label: 'Audit Investigation', icon: Blocks },
]

const DOCS_BOTTOM_ITEMS: SimpleItem[] = [
  { path: '/docs/best-practices', label: 'Best Practices', icon: BookOpen },
]

interface SectionBodyProps {
  pathname: string
  /** Called after any row is chosen, also the current page's (the phone drawer closes on it). */
  onItemSelect?: () => void
}

function SimpleListBody({
  items,
  pathname,
  onItemSelect,
}: SectionBodyProps & { items: SimpleItem[] }) {
  const navigate = useNavigate()
  const isActive = (p: string) => pathname === p || pathname.startsWith(p + '/')
  return (
    <div className="flex flex-col gap-0.5 p-2">
      {items.map(it => (
        <NavRow
          key={it.path}
          icon={it.icon}
          label={it.label}
          active={isActive(it.path)}
          onClick={() => {
            if (pathname !== it.path) navigate(it.path)
            onItemSelect?.()
          }}
        />
      ))}
    </div>
  )
}

/**
 * Featured launcher for the Extraction Demo mini-app. Deliberately styled
 * unlike the plain docs `NavRow`s — a bordered, primary-tinted card with a
 * filled icon badge, an "App" tag, and a launch arrow — so it reads as an
 * interactive tool rather than another article in the docs list.
 */
function DocsAppLauncher({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors',
        active
          ? 'border-primary/50 bg-primary/15'
          : 'border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/10'
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary ring-1 ring-inset ring-primary/30">
        <FlaskConical className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-foreground">Extraction Demo</span>
          <span className="rounded-sm bg-primary/20 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-primary">
            App
          </span>
        </span>
        <span className="block truncate text-[11px] text-muted-foreground">
          Interactive sandbox
        </span>
      </span>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-primary/60 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </button>
  )
}

function DocsSidebarBody({ pathname, onItemSelect }: SectionBodyProps) {
  const navigate = useNavigate()
  // Exact match only — important because /docs is a prefix of every other docs path.
  const isActive = (p: string) => pathname === p

  const renderItem = (it: SimpleItem) => (
    <NavRow
      key={it.path}
      icon={it.icon}
      label={it.label}
      active={isActive(it.path)}
      onClick={() => {
        if (pathname !== it.path) navigate(it.path)
        onItemSelect?.()
      }}
    />
  )

  return (
    <div className="flex flex-col">
      {/* Featured mini-app — pinned to the top, distinct from the docs articles. */}
      <div className="px-2 pt-2">
        <DocsAppLauncher
          active={isActive('/docs/extraction-demo')}
          onClick={() => {
            if (pathname !== '/docs/extraction-demo') navigate('/docs/extraction-demo')
            onItemSelect?.()
          }}
        />
      </div>
      <div className="mx-3 my-2 border-t border-border/60" />

      <div className="flex flex-col gap-0.5 px-2">{DOCS_TOP_ITEMS.map(renderItem)}</div>

      <div className="mt-1">
        <GroupLabel>Guides</GroupLabel>
        <div className="flex flex-col gap-0.5 px-2">{DOCS_GUIDE_ITEMS.map(renderItem)}</div>
      </div>

      <div className="mt-1">
        <GroupLabel>Pipelines</GroupLabel>
        <div className="flex flex-col gap-0.5 px-2">{DOCS_PIPELINE_ITEMS.map(renderItem)}</div>
      </div>

      <div className="mt-1">
        <GroupLabel>Schema Design</GroupLabel>
        <div className="flex flex-col gap-0.5 px-2">{DOCS_SCHEMA_ITEMS.map(renderItem)}</div>
      </div>

      <div className="mt-1 pb-2">
        <GroupLabel>Reference</GroupLabel>
        <div className="flex flex-col gap-0.5 px-2">{DOCS_BOTTOM_ITEMS.map(renderItem)}</div>
      </div>
    </div>
  )
}

function CaseSettingsFooter({
  caseId,
  activeSubView,
  pathname,
}: {
  caseId: string
  activeSubView: SubView | undefined
  pathname: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const url = `/cases/${caseId}/settings`
  return (
    <div className="shrink-0 border-t border-border px-2 py-2">
      <NavRow
        icon={Settings}
        label={t('nav.cases.caseSettings')}
        active={activeSubView?.path === 'settings'}
        onClick={() => {
          if (pathname !== url) navigate(url)
        }}
      />
    </div>
  )
}

/* ───────────────────────── Wrapper ───────────────────────── */

interface SectionNavProps {
  section: Section
  caseId: string | undefined
  activeWorkspace: Workspace | undefined
  activeSubView: SubView | undefined
  pathname: string
  onPickerOpenChange?: (open: boolean) => void
  /** Called after a sub-navigation row is chosen (admin, docs, settings lists). */
  onItemSelect?: () => void
}

interface SectionSidebarProps extends SectionNavProps {
  /** When true, sidebar is shown as a floating overlay (no collapse button). */
  asOverlay?: boolean
  onCollapse?: () => void
}

/**
 * A section's sub-navigation without any chrome — the body of the desktop
 * SectionSidebar, and the nested list under a section in the phone drawer.
 */
export function SectionNav({
  section,
  caseId,
  activeWorkspace,
  activeSubView,
  pathname,
  onPickerOpenChange,
  onItemSelect,
}: SectionNavProps) {
  const { t } = useTranslation()

  const translatedAdminItems: SimpleItem[] = [
    { path: '/admin/labels', label: t('nav.adminNav.labels'), icon: Shield },
    { path: '/admin/users', label: t('nav.adminNav.users'), icon: Users },
    { path: '/admin/schema', label: t('nav.adminNav.schema'), icon: Blocks },
    {
      path: '/admin/schema-playground',
      label: t('nav.adminNav.schemaPlayground'),
      icon: FlaskConical,
    },
    {
      path: '/admin/pipelines',
      label: t('nav.adminNav.pipelines'),
      icon: GitBranch,
    },
    {
      path: '/admin/processing',
      label: t('admin.processing.title'),
      icon: Activity,
    },
    {
      path: '/admin/audio',
      label: t('settings.sections.audio'),
      icon: AudioLines,
    },
    {
      path: '/admin/video',
      label: t('settings.sections.video'),
      icon: Video,
    },
  ]
  const translatedSettingsItems: SimpleItem[] = [
    {
      path: '/settings/profile',
      label: t('nav.settingsNav.profile'),
      icon: User,
    },
    {
      path: '/settings/appearance',
      label: t('nav.settingsNav.appearance'),
      icon: Palette,
    },
    {
      path: '/settings/sftp',
      label: t('nav.settingsNav.sftpAccess'),
      icon: TerminalSquare,
    },
    {
      path: '/settings/permissions',
      label: t('nav.settingsNav.permissions'),
      icon: Briefcase,
    },
  ]

  let body: React.ReactNode
  switch (section.key) {
    case 'cases':
      body = (
        <CasesSidebarBody
          caseId={caseId}
          activeWorkspace={activeWorkspace}
          activeSubView={activeSubView}
          pathname={pathname}
          onPickerOpenChange={onPickerOpenChange}
        />
      )
      break
    case 'data':
      // Data is a flat product (data lake) — AppShell skips the nested sidebar entirely.
      body = null
      break
    case 'admin':
      body = (
        <SimpleListBody
          items={translatedAdminItems}
          pathname={pathname}
          onItemSelect={onItemSelect}
        />
      )
      break
    case 'docs':
      body = <DocsSidebarBody pathname={pathname} onItemSelect={onItemSelect} />
      break
    case 'settings':
      body = (
        <SimpleListBody
          items={translatedSettingsItems}
          pathname={pathname}
          onItemSelect={onItemSelect}
        />
      )
      break
  }
  return body
}

export function SectionSidebar({ asOverlay = false, onCollapse, ...nav }: SectionSidebarProps) {
  const { t } = useTranslation()
  const { section, caseId, activeSubView, pathname } = nav
  // Desktop-only chrome: below md the same navigation lives in the drawer.
  const wrapperClass = cn(
    'hidden w-60 shrink-0 flex-col border-r border-border bg-background md:flex',
    asOverlay && 'absolute left-16 top-0 bottom-0 z-30 h-full shadow-2xl'
  )

  const caseSettingsFooter =
    section.key === 'cases' && caseId ? (
      <CaseSettingsFooter caseId={caseId} activeSubView={activeSubView} pathname={pathname} />
    ) : null

  return (
    <aside className={wrapperClass}>
      <SectionHeader
        title={t(`nav.sections.${section.key}`)}
        showCollapse={!asOverlay && section.key !== 'docs'}
        onCollapse={onCollapse}
      />
      <div className="flex-1 overflow-y-auto">
        <SectionNav {...nav} />
      </div>
      {caseSettingsFooter}
    </aside>
  )
}
